import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { CalendarIcon, User } from 'lucide-react';

const ALL_COLUMNS = [
  { key: 'To Do', color: 'border-t-gray-400', bg: 'bg-gray-50 dark:bg-gray-900/30' },
  { key: 'In Progress', color: 'border-t-blue-500', bg: 'bg-blue-50/50 dark:bg-blue-900/10' },
  { key: 'In Review', color: 'border-t-amber-500', bg: 'bg-amber-50/50 dark:bg-amber-900/10', ddOnly: true },
  { key: 'On Hold', color: 'border-t-purple-500', bg: 'bg-purple-50/50 dark:bg-purple-900/10', hrOnly: true },
  { key: 'Completed', color: 'border-t-green-500', bg: 'bg-green-50/50 dark:bg-green-900/10' },
  { key: 'Cancelled', color: 'border-t-red-500', bg: 'bg-red-50/50 dark:bg-red-900/10' },
];

const getPriorityStyle = (priority, dept) => {
  const isDD = dept === 'Design & Development';
  if (isDD) {
    if (priority === 1) return 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20';
    if (priority <= 3) return 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20';
    return 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20';
  }
  if (priority <= 3) return 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20';
  if (priority <= 6) return 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20';
  return 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20';
};

const getPriorityLabel = (priority, dept) => {
  const isDD = dept === 'Design & Development';
  if (isDD) {
    if (priority === 1) return 'High';
    if (priority <= 3) return 'Medium';
    return 'Low';
  }
  if (priority <= 3) return 'High';
  if (priority <= 6) return 'Medium';
  return 'Low';
};

const isOverdue = (dueDate, status) => {
  if (status === 'Completed' || status === 'Cancelled') return false;
  return new Date(dueDate) < new Date(new Date().setHours(0, 0, 0, 0));
};

const TaskCard = ({ task }) => {
  const overdue = isOverdue(task.due_date, task.status);
  return (
    <Link to={`/tasks/${task.id}`} className="block" data-testid={`kanban-card-${task.id}`}>
      <Card className={`p-3 hover:shadow-md transition-all cursor-pointer border-l-2 ${overdue ? 'border-l-red-500' : 'border-l-transparent'}`}>
        <p className="font-medium text-sm leading-tight line-clamp-2" data-testid={`kanban-task-name-${task.id}`}>
          {task.task_name}
        </p>
        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
          {task.work_type === 'Team' && task.department === 'Design & Development' && <span className="text-[9px] px-1 py-0 rounded bg-blue-500/15 text-blue-600 dark:text-blue-400 font-medium">Team</span>}
          {task.project_name && <span className="text-[9px] px-1 py-0 rounded bg-[#F5A623]/15 text-[#F5A623] font-medium">{task.project_name}</span>}
          {task.brand && <span className="text-[10px] text-muted-foreground">{task.brand}</span>}
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <Badge className={`text-[10px] px-1.5 py-0 border ${getPriorityStyle(task.priority, task.department)}`}>
            {getPriorityLabel(task.priority, task.department)}
          </Badge>
          {task.category && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{task.category}</Badge>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1 truncate max-w-[60%]">
            <User className="h-3 w-3 shrink-0" />
            <span className="truncate">{task.assigned_to_name || '-'}</span>
          </span>
          <span className={`flex items-center gap-1 ${overdue ? 'text-red-500 font-medium' : ''}`}>
            <CalendarIcon className="h-3 w-3 shrink-0" />
            {new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
          </span>
        </div>
      </Card>
    </Link>
  );
};

export const KanbanBoard = ({ tasks, showInReview = false, showOnHold = false }) => {
  const COLUMNS = ALL_COLUMNS.filter(c => (!c.ddOnly || showInReview) && (!c.hrOnly || showOnHold));
  const grouped = {};
  COLUMNS.forEach(col => { grouped[col.key] = []; });
  tasks.forEach(task => {
    if (grouped[task.status]) grouped[task.status].push(task);
  });

  const gridCols = COLUMNS.length === 6 ? 'lg:grid-cols-6' : COLUMNS.length === 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4';

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 ${gridCols} gap-3`} data-testid="kanban-board">
      {COLUMNS.map(col => (
        <div key={col.key} className={`rounded-lg border-t-4 ${col.color} ${col.bg} p-3 min-h-[200px]`} data-testid={`kanban-column-${col.key}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">{col.key}</h3>
            <span className="text-xs bg-background border rounded-full px-2 py-0.5 font-medium">
              {grouped[col.key].length}
            </span>
          </div>
          <div className="space-y-2">
            {grouped[col.key].length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">No tasks</p>
            ) : (
              grouped[col.key].map(task => (
                <TaskCard key={task.id} task={task} />
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
